"use client";

import React from "react";
import { useSetAtom } from "jotai";
import { usePathname } from "next/navigation";
import { Menu, Phone, FastForward, Bot } from "lucide-react";
import { SiWhatsapp } from "@icons-pack/react-simple-icons";

import { Button } from "@/components/ui/button";
import { Paragraph } from "@/components/ui/typography";
import Link from "@/components/link";

import useMoengageEventTracker from "@/hooks/use-moengage";
import EVENT_NAME from "@/hooks/use-moengage/event-name";
import EVENT_ATTRIBUTE from "@/hooks/use-moengage/event-attribute";

import { showContinuePurchaseModalAtom } from "@/store/modal";

import { cn } from "@/lib/utils";

function FloatingActionButton({ footerData }) {
  const pathname = usePathname();

  const trackMoengageEvent = useMoengageEventTracker();

  const [isOpen, setIsOpen] = React.useState(false);
  const showContinuePurchaseModal = useSetAtom(showContinuePurchaseModalAtom);

  return (
    <div
      className={cn("fixed bottom-12 right-6 z-50", {
        "bottom-40 lg:bottom-12": pathname.startsWith("/course-detail/"),
      })}
    >
      <div
        className={cn(
          "absolute bottom-1/2 right-0 invisible opacity-0",
          "w-45 transition-all duration-300 -z-1",
          { "visible opacity-100 bottom-[calc(100%+16px)]": isOpen }
        )}
      >
        <ul className="flex flex-col gap-y-3 [&>li]:w-full">
          <li>
            <Button
              variant="ghost"
              className="justify-start bg-primary-bg! rounded-2xl"
              block
              onClick={() => {
                showContinuePurchaseModal(true);
                setIsOpen(false);
                trackMoengageEvent(EVENT_NAME.SUPPORT_CLICKED, {
                  [EVENT_ATTRIBUTE.TYPE]: "continue_purchase",
                });
              }}
            >
              <FastForward />
              <Paragraph variant="b5">Continue Purchase</Paragraph>
            </Button>
          </li>
          <li>
            <Button
              variant="ghost"
              className="justify-start bg-primary-bg! rounded-2xl"
              block
              onClick={() => {
                window.fcWidget.open();
                setIsOpen(false);
                trackMoengageEvent(EVENT_NAME.SUPPORT_CLICKED, {
                  [EVENT_ATTRIBUTE.TYPE]: "help_clicked",
                });
              }}
            >
              <Bot />
              <Paragraph variant="b5">Help</Paragraph>
            </Button>
          </li>
          {footerData?.constantWeb && (
            <>
              <li className="hidden md:block">
                <Button
                  variant="ghost"
                  className="justify-start bg-primary-bg! rounded-2xl"
                  block
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        footerData.constantWeb.supportPhoneNumber
                      );
                    } catch (err) {
                      console.error("Failed to copy text: ", err);
                    }

                    trackMoengageEvent(EVENT_NAME.SUPPORT_CLICKED, {
                      [EVENT_ATTRIBUTE.TYPE]: "request_callback",
                    });
                  }}
                >
                  <Phone />
                  <Paragraph variant="b5">
                    {footerData.constantWeb.supportPhoneNumber}
                  </Paragraph>
                </Button>
              </li>

              <li className="block md:hidden">
                <Button
                  variant="ghost"
                  className="justify-start bg-primary-bg! rounded-2xl"
                  block
                  asChild
                >
                  <Link
                    href={`tel:${footerData.constantWeb.supportPhoneNumber}`}
                    onClick={() => {
                      trackMoengageEvent(EVENT_NAME.SUPPORT_CLICKED, {
                        [EVENT_ATTRIBUTE.TYPE]: "request_callback",
                      });
                    }}
                  >
                    <Phone />
                    <Paragraph variant="b5">Connect</Paragraph>
                  </Link>
                </Button>
              </li>
            </>
          )}
          <li>
            <Button
              variant="ghost"
              className="justify-start bg-primary-bg! rounded-2xl"
              block
              asChild
            >
              <Link
                href="https://wa.me/+919318480549"
                onClick={() => {
                  trackMoengageEvent(EVENT_NAME.SUPPORT_CLICKED, {
                    [EVENT_ATTRIBUTE.TYPE]: "request_callback",
                  });
                }}
              >
                <SiWhatsapp className="fill-primary" />
                <Paragraph variant="b5">WhatsApp</Paragraph>
              </Link>
            </Button>
          </li>
        </ul>
      </div>

      <Button
        size="icon"
        className="size-10 rounded-full overflow-hidden"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {isOpen ? (
          <Menu className="size-4" />
        ) : (
          <div
            className={cn(
              "relative flex items-center justify-center gap-3 size-full",
              "text-primary-foreground [&_svg]:size-4! animate-slide-steps duration-6000"
            )}
          >
            <FastForward />
            <Bot />
            <Phone />
            <FastForward />
          </div>
        )}
      </Button>
    </div>
  );
}

export default FloatingActionButton;
